import React from 'react';
import { useEffect, useState, useRef, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import DOMPurify from 'dompurify';
import Box from '@mui/material/Box';
import { DataGrid} from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import SaveAsOutlinedIcon from '@mui/icons-material/SaveAsOutlined';
import APICom from "../../../utils/helpers/APICom.js";
import { outlinedInputSX } from '../../../utils/helpers/DriverRouteMessaging/FormSelect.js';
import FormSelect from '../../../utils/helpers/DriverRouteMessaging/FormSelect.js';
import GridFooter, { buttonSX } from '../../../utils/helpers/DriverRouteMessaging/GridFooter.js';
import generateColumns from '../../../utils/helpers/DriverRouteMessaging/columns.js';
import Calendar from '../../../utils/helpers/DriverRouteMessaging/Calendar.js';
import dayjs from 'dayjs';
import PersistStorage from '../../../utils/helpers/PersistStorage.js';

var currentDate = new Date();
currentDate.setDate(currentDate.getDate() + 1);
var day = currentDate.getDate();
var month = currentDate.getMonth() + 1;
var year = currentDate.getFullYear();



const DriverRouteMessaging = React.memo((props) => {

    // Date state variable for calendar, defaults to Tomorrow
    const [fetchDate, setFetchDate] = useState(dayjs(year + "-" + month + "-" + day));
    
    // State variables for the routes and Redux data
    const [loadingRoutes, setLoadingRoutes] = useState(true);
    const [routes, setRoutes] = useState([]);
    const {DispatchVehicleData, AffiliateData, DispatchDriverData, GlobalASCSData, BrokerServingAffiliatesData, isLoading, } = useSelector((store) => store.combinedata);
    const globalInLoadAPIURL = (GlobalASCSData.find(dict => dict.SubTitle === "InLoadAPI_URL") ? GlobalASCSData.find(dict => dict.SubTitle === "InLoadAPI_URL").value : "");
    
    // Obtains list of affiliates available for the logged in user
    const { loginData }  = useSelector((store) => store.login);
    let loginUserAffiliateList = (loginData.UserAffiliateList.includes(",")? loginData.UserAffiliateList.split(",") : [loginData.UserAffiliateList]);
    
    // Obtains the selected affiliate from the dispatch screen
    const passedAff = PersistStorage.retrieve('passedAff', true);
    const [currentAffiliate, setCurrentAffiliate] = useState(loginUserAffiliateList.find(item => item === String(passedAff)) ?? '');
    
    // Used to handle affiliates amd brokers, allows us to distinguish them
    const tempAffiliate = AffiliateData.find(item => item.iOutTSPID.toString() === currentAffiliate.toString());
    let isBroker = (tempAffiliate && tempAffiliate.bBroker === 1) ? true : false;
    let finalAffiliate = useRef(isBroker ? tempAffiliate.iInTSPID : currentAffiliate);

    // Holds list of available transferring affiliates for a broker, and which is currently selected
    const [transferringAffiliates, setTransferringAffiliates] = useState([]);
    const [transferringAffiliate, setTransferringAffiliate] = useState('');

    // State variable for the rows in the DataGrid
    const [rows, setRows] = useState([]);

    // State variable for the message template
    const [messageTemplate, setMessageTemplate] = useState(PersistStorage.retrieve("messageTemplate", true) ?? 'Your route for {Date} starts at {Route Start}. Please confirm your availability.');

    // Row selection model, used for tracking which DataGrid rows are currently selected
    const [rowSelectionModel, setRowSelectionModel] = React.useState({
        type: 'include',
        ids: new Set(),
    });
    

    // Obtains the list of vehicles for the current affiliate
    const affiliateVehicleData = useRef(DispatchVehicleData[parseInt(currentAffiliate)] && 
    DispatchVehicleData[parseInt(currentAffiliate)] !== '' ? DispatchVehicleData[parseInt(currentAffiliate)] : [])

    // vehicles is used for the drop downs, vehiclesWithID is used for obtaining vehicleID for API calls
    const vehicles = useRef(affiliateVehicleData.current.length === 0 ? [] : [...new Set(JSON.parse(affiliateVehicleData.current).map(vehicle => (vehicle.VehicleNumber)))]);
    const vehiclesWithID = useRef(affiliateVehicleData.current.length === 0 ? [] : [...new Set(JSON.parse(affiliateVehicleData.current).map(vehicle => ({vehicleNo: vehicle.VehicleNumber, vehicleID: vehicle.VehicleID})))]);
    
    // Obtains the list of drivers for the current affiliate
    const affiliateDriverData = useRef(DispatchDriverData[parseInt(currentAffiliate)] ? JSON.parse(DispatchDriverData[parseInt(currentAffiliate)]) : []);
    const drivers = useRef(Array.from([...new Set(affiliateDriverData.current.map(driver => ({Name: driver.DriverName, Phone: driver.DriverPhone, Email: driver.DriverEmail, DriverID: driver.DriverID})))]));

    // References used for updating the DataGrid, mainly used for properly updating Response column
    const rowsRef = useRef(rows);
    const selectionRef = useRef(rowSelectionModel);
    const dateRef = useRef(dayjs(fetchDate).format('YYYY-MM-DD'));
    const pollTimerRef = useRef(null);
    const isPollingRef = useRef(false);
    useEffect(() => { rowsRef.current = rows; }, [rows]);
    useEffect(() => { selectionRef.current = rowSelectionModel; }, [rowSelectionModel]);
    useEffect(() => { dateRef.current = dayjs(fetchDate).format('YYYY-MM-DD'); }, [fetchDate]);


    // References for list of responses from a Driver's cell number
    const finalResponses = useRef([]);
    const [finalResponsesState, setFinalResponsesState] = useState([]);

    // Used for restricting driver selection to one row
    const assignedDriverNames = useMemo(
        () => new Set(rows.filter(r => r.driver).map(r => r.driver)),
           [rows]
    );
    // Obtains column definition for DataGrid, see columns.js
    const columns = generateColumns(vehicles, drivers, handleVehicleChange, handleDriverChange, handleMessageBody, assignedDriverNames);


    // Handles events to take place when affiliate changes
    // Such as: start route loader, and determine of affiliate is broker
    useEffect(() => {
        setLoadingRoutes(true);
        getRoutes();
        isBroker = AffiliateData.find(item => item.iOutTSPID.toString() === currentAffiliate.toString())?.bBroker === 1 ? true : false;

    }, [currentAffiliate,]);
      
    // Obtains new list of routes if the date is changed
    useEffect(() => {
        setLoadingRoutes(true);
        getRoutes();

    }, [ fetchDate,]);

    // Triggers the building and sorting of the DataGrid's Rows
    // Also filters down the routes based on the selected transferring affiliate
    useEffect(() => {
        if (!routes.length) {
          setRows([]); 
          setLoadingRoutes(false);
          return;
        }
      
        setLoadingRoutes(true);
        let sorted = [...routes].sort((a,b) =>
          minutesSinceMidnight(a.time) - minutesSinceMidnight(b.time)
        );


        if (isBroker && transferringAffiliate !== ''){
            
            sorted.filter(item => item.brokerRoutes?.some(route => route.iAffiliateID === transferringAffiliate));
        }

        const newRows = sorted.map(r => {
          const row = {
            id:        r.id,
            routeTime: r.time,
            vehicleNo: r.vehicle ?? '',
            driver:    '',
            phone:     '--------',
            email:     '--------',
            response:  ''
          };
          return {
            ...row,
            sms: replaceSmsVars(messageTemplate, row)
          };
        });
      
        setRows(newRows);
        //fetchDriverResponses();
        
        setLoadingRoutes(false);
    }, [routes, transferringAffiliate,]);
      
    // Handles obtaining new vehicles & drivers based on the selected transferring affiliate
    useEffect(() => {
        
        if (isBroker && transferringAffiliate !== ''){

            affiliateVehicleData.current = DispatchVehicleData[parseInt(transferringAffiliate)] && 
            DispatchVehicleData[parseInt(transferringAffiliate)] !== '' ? DispatchVehicleData[parseInt(transferringAffiliate)] : []

            vehicles.current = affiliateVehicleData.current.length === 0 ? [] : [...new Set(JSON.parse(affiliateVehicleData.current).map(vehicle => (vehicle.VehicleNumber)))];
            vehiclesWithID.current = affiliateVehicleData.current.length === 0 ? [] : [...new Set(JSON.parse(affiliateVehicleData.current).map(vehicle => ({vehicleNo: vehicle.VehicleNumber, vehicleID: vehicle.VehicleID})))];

            affiliateDriverData.current = DispatchDriverData[parseInt(transferringAffiliate)] ? JSON.parse(DispatchDriverData[parseInt(transferringAffiliate)]) : [];
            drivers.current = Array.from([...new Set(affiliateDriverData.current.map(driver => ({Name: driver.DriverName, Phone: driver.DriverPhone, Email: driver.DriverEmail, DriverID: driver.DriverID})))]);
        
        }

       
        finalAffiliate.current = isBroker ? transferringAffiliate : currentAffiliate;
    }, [transferringAffiliate])

    // Triggers an update in the Message Body column whenever the template is changed
    useEffect(() => {
        setRows(prev =>
            prev.map(row => ({
            ...row, 
            sms: replaceSmsVars(messageTemplate, row)
            }))
        );
    }, [messageTemplate]);

    //Handles the polling of the Driver's responses if the selected rows change
    useEffect(() => {
        fetchDriverResponses();
        return () => {
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
    }, [routes, transferringAffiliate])

    // Handles insertion of Driver's responses into the DataGrid based on row phone numbers
    useEffect(() => {
        finalResponses?.current?.forEach(response =>{
            response?.forEach(innerResponse => {
                if (innerResponse.SMSResponse !== ""){
                    setRows(prevRows => {
                        const updatedRows = prevRows.map(prevRow => {
                            if (prevRow.phone.replace(/-/g, '') === innerResponse.vDriverPhone.replace(/-/g, '')) {
                            return {
                                ...prevRow,
                                response: innerResponse.SMSResponse
                            };
                            }
                            return prevRow;
                        });
                        return updatedRows;
                    });
                }
            })
        })
    }, [finalResponsesState])


    // Final return statement, this builds the UI for the entire page
    return(
        <Box sx={{width: '99vw', overflowY: 'scroll'}}>
            { ((isBroker && finalAffiliate.current) || !isBroker) ? 
            (   <>
                <Calendar
                    fetchDate={fetchDate}
                    setFetchDate={setFetchDate}
                />

                {loginUserAffiliateList.length > 0 && 
                <FormSelect
                    value={currentAffiliate}
                    onChange={e => setCurrentAffiliate(e.target.value)}
                    options={AffiliateData.filter(item =>
                        loginUserAffiliateList.includes(String(item.AffiliateID)))}
                    getOptionKey={item => item.AffiliateID}
                    getOptionValue={item => item.AffiliateID}
                    getOptionLabel={item => item.AffiliateCompany}
                    label="Affiliate/Broker"
                    sx={{ width: 350, margin: 1, marginTop: 2}}
                />}

                {(isBroker && transferringAffiliates.length > 0) &&
                <FormSelect
                    value={transferringAffiliate}
                    onChange={e => {setTransferringAffiliate(e.target.value);}}
                    options={transferringAffiliates.filter(item => item.iAffiliateID)}
                    getOptionKey={item => item.iAffiliateID}
                    getOptionValue={item => item.iAffiliateID}
                    getOptionLabel={item => item.vName}
                    label="Transferring Affiliate"
                    sx={{ width: 350, margin: 1, marginTop: 2}}
                />}

                <Box display="flex" justifyContent="center" alignItems="center" 
                    sx={{height: '70vh', 
                        '& .super-app.positive': {
                            backgroundColor: 'rgba(157, 255, 118, 0.49)', // Green background for non-empty responses
                            fontWeight: '400',
                        },
                        '& .super-app.negative': {
                            backgroundColor: 'rgba(255, 99, 99, 0.49)', // Red background for empty responses (optional)
                            fontWeight: '400',
                        },
                        '& .super-app.unknown': {
                            backgroundColor: 'rgba(243, 231, 7, 0.49)', // Yellow background for unknown
                            fontWeight: '400',
                        },
                    }}
                >
                    {loadingRoutes ? 
                        (<>
                            <Spinner
                                as="span" animation="border" size="sm"
                                role="status"
                                aria-hidden="true" aria-busy= {isLoading}
                                style={{ marginBottom: '3px', top: '50%', left: '50%' }}
                            />
                            <label style={{marginLeft: "10px",fontSize: "larger",marginBottom: "5px"}}><output>Please wait, Loading Data....</output></label>
                        </>)
                        :
                        <DataGrid
                            processRowUpdate={(updatedRow, originalRow) => {
                                return handleRowEdit(updatedRow, originalRow);
                            }}

                            getRowClassName={(params) =>
                                params.indexRelativeToCurrentPage % 2 === 1 ? 'odd-row' : ''}
                            
                            getRowHeight={(params) => {
                                // Prevents auto scrolling due to assumed row heights
                                const smsText = params.model.sms;
                                const charsPerLine = 80;
                                const lineCount = Math.ceil(smsText.length / charsPerLine) > 5 ? 5 : Math.ceil(smsText.length / charsPerLine);
                                const baseHeight = 70; 
                                const lineHeight = 30; 
                                
                                return baseHeight + lineCount * lineHeight;
                            }}
                            
                            rows={rows}
                            columns={columns}
                            pagination={false}
                            checkboxSelection
                            disableRowSelectionOnClick
                            onRowSelectionModelChange={(newRowSelectionModel) => {
                                setRowSelectionModel(newRowSelectionModel);
                            }}
                            rowSelectionModel={rowSelectionModel}

                            sx={{
                                
                                '& .superCentered': {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                },
                                '& .odd-row': {
                                    backgroundColor: theme => theme.palette.grey[200],
                                },
                                '& .MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within': {
                                    outline: '1px solid var(--headerfooter-bg-color)',
                                    outlineOffset: '-1px',
                                },
                                '& .MuiDataGrid-cell:focus': {
                                    outline: '1px solid var(--headerfooter-bg-color)',
                                    outlineOffset: '-1px',
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: 'rgba(50, 114, 139, .25) !important',
                                },
                            }}

                            slots={{
                                footer: GridFooter,
                            }}
                            slotProps={{
                                footer:{
                                    rows,
                                    drivers,
                                    vehiclesWithID,
                                    setRows,
                                    messageTemplate,
                                    rowSelectionModel,
                                    globalInLoadAPIURL,
                                    finalAffiliate,
                                    replaceSmsVars
                                }
                            }}
                        />
                    }
                </Box>

                <Box 
                    sx={{
                        display: 'flex', alignItems: 'flex-end',     
                        pl: '3%', mt: 2, mb: 10,                    
                        width: '100%',
                        gap: 1             
                    }}
                >
                    <TextField 
                        sx={{width: '84%', ...outlinedInputSX}} 
                        label="Message Template" 
                        multiline
                        value={messageTemplate}
                        onChange={((e) => {handleMessageTemplateChange(e.target.value);})}
                    />

                    <Button variant="outlined" endIcon={<SaveAsOutlinedIcon/>}
                        sx={{...buttonSX}}
                        onClick={() => PersistStorage.save("messageTemplate", messageTemplate, 10080, true)}
                    >
                        Save
                    </Button>
                </Box>
                </>) 
                :
                (<span
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 999,
                    }}
                    >
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            aria-busy= {isLoading}
                            style={{ marginBottom: '3px' }}
                        />
          
                        <label style={{marginLeft: "10px",fontSize: "larger",marginBottom: "5px"}}><output>Please wait, Loading Data....</output></label>
                    </span>)}
        </Box>
    );

    /**
     * Fetches all routes for the given date and affiliate from the API and processes the
     * response to store the routes in the state. If the user is a broker, it also fetches
     * the transferable affiliates for each route and stores them separately in the state.
     * @returns {Promise<void>}
     */
    async function getRoutes(){
        try{
            setRoutes([]);
            const routeResponse = await APICom.fetchData("GenericManifestDotNetCoreURL", 
                `api/Routing/GetAllTodaysRoutes?selected_date=${dayjs(fetchDate).format('YYYY-MM-DD')}&affiliate_id=${currentAffiliate}
                &iTransType=0&bMainTables=true`, "get", { "Content-Type": "application/json" }, null, null);

            console.log("routes", routeResponse)

            let tempRoutes = [];
            let tempTransferringAffiliates = [];

            if(routeResponse && routeResponse.error.length === 0 && routeResponse.data !== null){
                
                for (let route of routeResponse.data){
                    if (route.RouteType === 'R'){
                        
                    }
                    const tempDate = new Date(route.dtRouteStartTime);
                    const time = tempDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                    const tempId = route.vVirtualRoute;
                    const vehicle = route.AssignedVehicle

                    if (isBroker && route.iRouteID !== null){
                        let editData = {
                            "route": tempId,
                            "dtSelectedDate": fetchDate,
                            "iSettingID": route.iSettingID,
                            "iAffilaiteID": parseInt(route.iAffiliateID),
                            "vManifestNumber": route.vManifestNumber?.length > 0 ? route.vManifestNumber : (`${fetchDate.get('year')}${fetchDate.get('month') + 1}${fetchDate.get('date')}` + tempId),
                            "iRouteID": route.iRouteID 
                          };
                        const response = await APICom.fetchData("GenericManifestDotNetCoreURL", "api/Routing/GetRouteSettings", "post", {}, editData, null);
                        
                        const responseData = await response?.data;
    
                        if (responseData !== undefined){ 
    
                            const transferrable = responseData.TransferingAffiliates;
                        
                            for (let i = 0; i < transferrable?.length; i++){
                                
                                if (!(tempTransferringAffiliates.some(existing => JSON.stringify(existing) === JSON.stringify(transferrable[i])))) {
                                  tempTransferringAffiliates.push(transferrable[i]);
                                }
                            }
                        }
                        
                        tempRoutes.push({
                            id: tempId,
                            time: time,
                            vehicle: vehicle,
                            brokerRoutes: responseData.TransferingAffiliates
                        })
                    }
                    else{
                            tempRoutes.push({
                            id: tempId,
                            time: time,
                            vehicle: vehicle
                        })
                    }
                    
                    
                }
                if (isBroker){
                    setTransferringAffiliates(tempTransferringAffiliates);
                    setTransferringAffiliate(tempTransferringAffiliates[1].iAffiliateID);
                }
                tempRoutes = assignUniqueIds(tempRoutes);
                setRoutes(tempRoutes);
            }
        }
        catch(err){
            console.log(err);
            setLoadingRoutes(false);
        }
    };

    /**
     * Handles changes to the driver dropdown in the message template editor. 
     * If the selected driver is valid, updates the driver's name, phone, and email in the message template.
     * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event.
     * @param {string} route - The route ID.
     * @param {string} routeTime - The route time.
     */
    function handleDriverChange(e, route, routeTime){
        

        let fullDriver = {}; 
        
        drivers.current.forEach((driver) => {
            if (driver.Name === e.target.value){
                fullDriver = driver;
            }
        });

        if (Object.keys(fullDriver).length !== 0) {
            const updatedName = fullDriver.Name;
            const updatedPhone = fullDriver.Phone;
            const updatedEmail = fullDriver.Email;

            const updatedRows = rows.map((row) => {
                if (row.id === route && row.routeTime === routeTime){
                    return {
                        ...row,
                        phone: updatedPhone, driver: updatedName, email: updatedEmail,
                        sms: replaceSmsVars(messageTemplate, {
                        ...row,
                        phone: updatedPhone, driver: updatedName, email: updatedEmail
                        })
                    };
                }
                return row;
            });
            setRows(updatedRows);
        }
    };

    /**
     * Handles changes to the SMS message body in the message editor.
     * If the selected driver is valid, updates the message body in the message template.
     * @param {React.ChangeEvent<HTMLTextAreaElement>} e - The change event.
     * @param {object} row - The row object from the DataGrid.
     */ 
    function handleMessageBody(e, row){

        const rowId = row.id;
        const routeTime = row.routeTime;
        const updatedSms = replaceSmsVars(e.target.value, row);

        const updatedRows = rows.map((row) => {
            if (row.id === rowId && row.routeTime === routeTime){
                return { ...row, sms: updatedSms};
            }
            return row;
        });
        setRows(updatedRows);
    };

    /**
     * Handles changes to the vehicle dropdown in the message template editor. 
     * If the selected vehicle is valid, updates the vehicle number in the message template.
     * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event.
     * @param {string} rowId - The row ID.
     * @param {string} routeTime - The route time.
     */
    function handleVehicleChange(e, rowId, routeTime){
        const updatedRows = rows.map((row) => {
            if (row.id === rowId && row.routeTime === routeTime){
                const updatedVeh = e.target.value;

                const updatedRow = {
                    ...row,
                    vehicleNo: updatedVeh,
                    sms: replaceSmsVars(messageTemplate, {
                      ...row,
                      vehicleNo: updatedVeh
                    })
                  };
                  return updatedRow;
            }
            return row;
        });
        setRows(updatedRows);
        
    };

    /**
     * Handles changes to the SMS message template. Updates the message template state
     * and recalculates the message body in each row of the DataGrid based on the updated
     * message template.
     * @param {React.ChangeEvent<HTMLTextAreaElement>} e - The change event.
     */
    function handleMessageTemplateChange(e){
        setMessageTemplate(e);

        setRows(rs =>
            rs.map(r => ({
              ...r,
              sms: replaceSmsVars(e, r)
            }))
          );
    };

    /**
     * Handles a row edit event from the DataGrid. Updates the row's 'sms' property by
     * recalculating the message body using the updated row values and the current
     * message template.
     * @param {object} updatedRow - The updated row object.
     * @param {object} originalRow - The original row object.
     * @returns {object} The updated row object.
     */
    function handleRowEdit(updatedRow, originalRow) {
        const updatedRows = rows.map(row => {
            if (row.id === originalRow.id ) {
                updatedRow.sms = replaceSmsVars(updatedRow.sms, updatedRow);
                return updatedRow;
            }
            return row;
        });
        setRows(updatedRows);

        return updatedRow;
    };

    /**
     * Calculates the number of minutes since midnight for a given time string.
     * The time string is expected to be in 12 hour format with an AM/PM modifier.
     * @param {string} ts - The time string.
     * @returns {number} The number of minutes since midnight.
     */
    function minutesSinceMidnight(ts) {
        let [time, modifier] = ts.split(' ');
        let [hrs, mins] = time.split(':').map(Number);
      
        if (modifier === 'PM' && hrs !== 12) hrs += 12;
        if (modifier === 'AM' && hrs === 12) hrs = 0;
      
        return hrs * 60 + mins;
    };

    /**
     * Replaces placeholders in a given SMS message template with values from a given row object.
     * The row object is expected to have properties for the vehicle number, driver name, phone number, email address, route ID, route start time, response, and message body.
     * The template string is sanitized using DOMPurify before replacement.
     * @param {string} template - The SMS message template.
     * @param {object} row - The row object containing the values to replace placeholders with.
     * @returns {string} The replaced template string.
     */
    function replaceSmsVars(template, row) {
        let sms = DOMPurify.sanitize(template);
      
        const map = {
            'Vehicle #':   row.vehicleNo,
            'Driver Name': row.driver,
            'Phone #':     row.phone === '--------' ? '{Phone #}' : row.phone,
            'Email':       row.email === '--------' ? '{Email}' : row.email,
            'Route':       row.id === '--------' ? '{Route}' : row.id,
            'Route Start': row.routeTime === '--------' ? '{Route Start}' : row.routeTime,
            'Message Body': row.sms,
            'Response':    row.response,
            'Date':        dayjs(fetchDate).format('YYYY-MM-DD')
          };
      
        Object.entries(map).forEach(([key, val]) => {
          sms = sms.replace(new RegExp(`{${key}}`, 'gi'), val ?? '');
        });
      
        return sms;
    };

    /**
     * Fetches driver responses from the server. This function is used to poll the server
     * for responses from drivers. It is called when the user selects a route and clicks
     * the "Send" button. It then periodically polls the server for responses until the
     * user navigates away from the route messaging page or the user stops polling.
     * @returns {void}
     */
    async function fetchDriverResponses(){
        if (isPollingRef.current) return;
            isPollingRef.current = true;

        try {
            const checkResponseURL = `${globalInLoadAPIURL}/TripReservation/GetDriverRouteMessaging`;
            const currentRows  = rowsRef.current;

            if (currentRows.length) {
                const body = {
                    idriverid: -1,
                    vdriverphone: "",
                    dtdate: dayjs().format('YYYY-MM-DD'),
                    iaffiliateid: finalAffiliate.current
                };

                try {
                    const response = await APICom.fetchData(
                        'full',
                        checkResponseURL,
                        'post',
                        { 'Content-Type': 'application/json' },
                        JSON.stringify(body),
                        null
                    );

                    let allResponses = [];
                    // Ensure the response data is a non-empty string before parsing
                    if (response.error.length === 0 && typeof response.data['Data'] === 'string' && response.data['Data'].length > 2) {
                        allResponses = JSON.parse(response.data['Data']);
                    }

                    finalResponses.current = allResponses.length > 0 ? [allResponses] : [];
                    
                    removeDuplicates(finalResponses.current);
                    setFinalResponsesState(finalResponses.current);

                } catch (error) {
                    console.error('Error fetching or processing driver responses:', error);
                }
            }
        } catch (e) {
            console.log('poll error', e);
        } finally {
            isPollingRef.current = false;
            pollTimerRef.current = setTimeout(fetchDriverResponses, 5000);
        }
    }

    /**
     * Assigns unique IDs to routes, by appending -Duplicate-<N> to the original
     * ID if there are multiple routes with the same ID.
     * @param {Array<Object>} routes
     * @returns {Array<Object>} The routes with unique IDs.
     */
    function assignUniqueIds(routes) {
        
        const totalById = new Map();
        for (const r of routes) {
          totalById.set(r.id, (totalById.get(r.id) || 0) + 1);
        }
      
        
        const seenById = new Map();
      
        return routes.map(r => {
          const total = totalById.get(r.id) || 0;
          if (total <= 1) {
            return { ...r, baseId: r.id };
          }
          const next = (seenById.get(r.id) || 0) + 1;
          seenById.set(r.id, next);
          return { ...r, baseId: r.id, id: `${r.id}-Duplicate-${next}` };
        });
      }

    function removeDuplicates(arr) {


        const filteredByDate = arr
        .map(subArr =>
            subArr.filter(obj => {
            const day = dayjs(obj.dtSMSMessageDateTime).add(1, 'day').format('YYYY-MM-DD');
            const match = day === dayjs(fetchDate).format('YYYY-MM-DD');
            console.log(day, dayjs(fetchDate).format('YYYY-MM-DD'), match)
            return match;
            })
        )
        .filter(subArr => subArr.length > 0);


        if (filteredByDate.length){
            const seen = new Set();
            const tempArr = filteredByDate.filter(subArr => {
                const stringified = JSON.stringify(subArr);
                if (seen.has(stringified)) {
                return false;
                } else {
                seen.add(stringified);
                return true;
                }
            });

            setRows(prevRows => {
                const updatedRows = prevRows.map(prevRow => {
                    let newArr = null;

                    // Loop through each sub-array in tempArr
                    for (let subArr of tempArr) {
                        // Find the matching object in the current sub-array using subArr['vVirtualRoute']
                        newArr = subArr.find((obj) => obj['vVirtualRoute'] === prevRow.id);
                        
                        // If we find a match, break the loop
                        if (newArr) break;
                    }

                    if (newArr !== undefined){
                        console.log("made it")
                        return {
                            ...prevRow,
                            phone: newArr.vDriverPhone,
                            response: newArr.SMSResponse,
                            driver: drivers.current.find(driver => driver.DriverID === newArr.iDriverID).Name,
                            vehicleNo: vehiclesWithID.current.find(vehicle => vehicle.vehicleID === newArr.iVehicleID).vehicleNo,
                        }
                    }
                    return prevRow;
                })
                return updatedRows;
            })
        }  
    }
});

export default DriverRouteMessaging;
    